import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { getRoles, createRole, updateRole, deleteRole } from '../api/roleAPI';
import { fetchData } from '../utils/apiHelpers';

const RolesListLayer = () => {
  const userProfile = useUserProfile();
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [toastMsg, setToastMsg] = useState('');

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const isSuperAdmin = userProfile?.roles?.some(r => r.id === 1);

  const fetchRolesData = () => {
    fetchData(getRoles, {}, data => {
      const filtered = isSuperAdmin ? data : data.filter(r => r.id !== 1);
      setRoles(filtered);
    });
  };

  useEffect(() => {
    fetchRolesData();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (roles.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [roles]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  useEffect(() => {
    if (toastMsg) {
      const t = setTimeout(() => setToastMsg(''), 5000);
      return () => clearTimeout(t);
    }
  }, [toastMsg]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRole(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errs = {};
    if (!newRole.name?.trim()) errs.name = 'Name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleEdit = (role) => {
    if (role.isAssignedToUser) {
      setToastMsg('Cannot edit, role is assigned to active users');
      return;
    }
    setNewRole(role);
    setEditingId(role.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const role = roles.find((item) => item.id === id);
    if (role?.isAssignedToUser) {
      setToastMsg('Cannot delete, role is assigned to active users');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await deleteRole(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      fetchRolesData();
    } catch (error) {
      console.error('Error deleting role:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      if (editingId) {
        await updateRole(editingId, newRole);
      } else {
        await createRole(newRole);
      }
      setShowModal(false);
      setEditingId(null);
      setNewRole({});
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      fetchRolesData();
    } catch (error) {
      console.error('Error saving role:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      {toastMsg && (
        <div className='toast-container position-fixed top-0 start-50 translate-middle-x p-3' style={{ zIndex: 2000 }}>
          <div className='toast show align-items-center text-bg-danger border-0'>
            <div className='d-flex'>
              <div className='toast-body'>{toastMsg}</div>
              <button type='button' className='btn-close btn-close-white me-2 m-auto' onClick={() => setToastMsg('')}></button>
            </div>
          </div>
        </div>
      )}
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('roles:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewRole({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Role
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td><strong>{role.name}</strong></td>
                  <td>{role.description}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('roles:update') && role.id !== 2 && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(role)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('roles:delete') && role.id !== 2 && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(role.id)}
                          disabled={role.isAssignedToUser}
                          title={role.isAssignedToUser ? 'Cannot delete, role is assigned to active users' : 'Delete role'}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Role' : 'Add Role'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name="name"
                    value={newRole.name || ''}
                    onChange={handleInputChange}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={newRole.description || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesListLayer;
